import { createContext } from "react";
import { isOldRoadWidth, roadWidthText } from "../../config/roadWidthOptions";

/**
 * Where the note is shown, which decides what it asks the reader to do:
 *   edit     - the reviewer's Edit Application page, under the dropdown;
 *   review   - an L1 review screen with "Select Change Fields";
 *   resubmit - the applicant's Resubmit page, when the field was returned.
 */
export type TRoadWidthNoteVariant = "edit" | "review" | "resubmit";

/**
 * Tells the road width dropdown (RenderSelect) which page it is on:
 *   "edit"     - the old value stays listed and selected, with the edit note;
 *   "resubmit" - the old value is cleared and only the four widths are
 *                listed, so the applicant must choose one, with the
 *                resubmit note;
 *   null       - any other form: the old value stays listed, no note.
 * A context is used so the pages that need it say so once, without passing
 * a prop through every RenderField of RenderStep.
 */
export const RoadWidthNoteContext = createContext<"edit" | "resubmit" | null>(null);

type Props = {
  /** The road width the application holds, as stored. */
  value: unknown;
  variant: TRoadWidthNoteVariant;
  className?: string;
};

/**
 * A yellow note shown only when the application holds an old road width
 * (see config/roadWidthOptions.ts); nothing is drawn otherwise.
 */
export function RoadWidthNote({ value, variant, className = "" }: Props) {
  if (!isOldRoadWidth(value)) return null;
  const oldValue = roadWidthText(value);

  return (
    <div
      role="note"
      className={`border border-yellow-400 bg-yellow-50 text-yellow-900 rounded-lg px-3 py-2 text-sm ${className}`}
    >
      {variant === "edit" && (
        <>
          Old road width: <b>"{oldValue}"</b>. Choose 6 m, 7.5 m, 9 m or &gt; 9 m, or return the
          application so the applicant can correct it.
        </>
      )}
      {variant === "review" && (
        <>
          Road width <b>"{oldValue}"</b> is from the old form. To have the applicant correct it,
          tick <b>"Existing Road width approaching to plot"</b> under <i>Select Change Fields</i>{" "}
          and press <b>Return</b>.
        </>
      )}
      {variant === "resubmit" && (
        <>
          Your earlier road width was <b>"{oldValue}"</b>. Please select the road width from the
          list: 6 m, 7.5 m, 9 m or &gt; 9 m.
        </>
      )}
    </div>
  );
}
